import { useContext } from 'react';
import GameContext from 'contexts/GameContext';
import DemoVoteContext from 'demo/DemoVoteContext';
import useGameService from 'services/GameService';

import ActionButton from 'components/UserActions/PokerActions/ActionButton';
import { useServerTimeout } from 'components/UserActions/ServerTimeoutBanner';

import { RUN_VARIANT } from './runVariants';
import { runLabel } from './voteFromAction';

/**
 * One run-count button: RUN IT 1X / 2X / 3X.
 *
 * EN-663: the vote buttons are the action row's `ActionButton` (same fill, stitch
 * edge, press animation) rather than their own MUI theme, so the vote reads as
 * the same control system as Check/Call/Raise.
 *
 * REPLACES VoteNoAction / VoteTwiceAction / VoteThriceAction, which were three
 * copies of this component differing only in label, variant and vote value. They
 * could not stay: AC-3.2 gives a responder 1X plus WHICHEVER count was offered,
 * so the set is now computed (see `offeredRunCounts`) rather than hard-coded, and
 * a fixed component per count cannot express that.
 *
 * `committing={false}`: a vote is not a poker action, so it must not hide the
 * turn timer — the seat's ring is counting the vote window down (AC-2.2's
 * "standard turn presentation") and hiding it would remove the countdown half.
 */

// AC-5.2's colour per count lives in runVariants.ts, shared with the felt's
// RUN IT text (EN-10856) so a count is one colour everywhere.

/**
 * The wire value for a run count.
 *
 * 1X sends '-2', the ORIGINAL decline value, rather than the '1' the servers also
 * now accept. Both writers treat them identically, so this is a free choice — and
 * '-2' is the one already threaded through `voteFromSeatAction`, `reconcileVote`
 * and the seat-action echo, so sending it keeps a label change from turning into
 * a reconciliation change. Nothing reads the wire value as copy.
 */
const voteValue = (runs: number): string => (runs === 1 ? '-2' : String(runs));

const VoteRunAction = ({ runs }: { runs: number }) => {
  const { multiRunVote } = useGameService();
  const { playerMultiRunVote } = useContext(GameContext);
  // Only the /demo-table harness provides this (see DemoVoteContext).
  const demoVote = useContext(DemoVoteContext);
  // A vote refused for a dead socket has no other way to reach the player:
  // the emit is fire-and-forget, so there is no ack timeout behind it.
  const { showServerTimeout } = useServerTimeout();

  const vote = voteValue(runs);

  return (
    <ActionButton
      variant={RUN_VARIANT[runs] ?? 'check'}
      // EN-10856: the vote is the table's decision panel, so its buttons are
      // the large size. The visible label stays the accessible name, so a
      // voice-control user can say what they see ("RUN IT 2X").
      size='decision'
      committing={false}
      // The panel unmounts on select (AC-2.2: "the buttons dismiss exactly like
      // normal action buttons"), so this only covers the frame between the click
      // and that unmount, plus the case where the echo rolls a vote back and the
      // buttons legitimately return.
      disabled={playerMultiRunVote !== '0' && playerMultiRunVote !== vote}
      onClick={() => (demoVote ? demoVote(vote) : multiRunVote(vote, showServerTimeout))}>
      {runLabel(runs)}
    </ActionButton>
  );
};

export default VoteRunAction;
